/**
 * I claim funzionali dell'utente: **da dove arrivano** e **come si chiamano**.
 *
 * Due cose che è costato scoprire, entrambe invisibili dal codice che li consuma.
 *
 * 1. **Non stanno nel JWT.** Il token emesso dal login porta sei claim tecnici —
 *    `userId`, `username`, `salt`, `superadmin`, `tenantId`, `masterTenantId` — e la riga
 *    che aggiungerebbe i claim di ruolo è commentata nel BE (`JwtSignInManager.cs:418`).
 *    I claim funzionali viaggiano nell'envelope di login/refresh, in `data.claimsList`:
 *    una stringa separata da virgole prodotta da `GetClaimsByUsername`, cioè **la stessa
 *    sorgente** che il BE usa lato server in `DomainAccessGuard`. Finché non li leggevamo
 *    da lì, ogni `claims.includes(...)` del FE era `false` per chiunque non fosse
 *    super-admin: non un permesso negato, un controllo che non esisteva.
 *
 * 2. **Quattro sono stati rinominati** nel deploy BE del 06-08-2026, senza finestra di
 *    compatibilità (`BE-DECISIONS-TO-FE-CLAIM-MAP-CANSEE-v1`), e `canSeeWeeklyPlanner` +
 *    `canSeeAllPlanning` sono stati consolidati in un interruttore solo. La migration ha
 *    riscritto le righe a DB, quindi i nomi vecchi non esistono più lato server — ma un
 *    token già emesso continua a portarli finché non scade. Gli alias qui sotto coprono
 *    quella finestra e nient'altro: quando l'ultimo token pre-06-08 è scaduto si tolgono.
 */

/** Un claim org-wide: apre la lettura su tutta l'azienda per il suo modulo. */
export const CLAIM = {
  /** Assenze di chiunque nel tenant (era `canSeeAllUsersAbsence`). */
  leaveReadAll: 'LeaveRequest.readAll',
  /** Presenze di chiunque nel tenant (era `canSeeAllUsersAttendance`). */
  attendanceReadAll: 'Attendance.readAll',
  /** Rendicontazione di chiunque nel tenant (era `canSeeAllReporting`). */
  timesheetReadAll: 'TimesheetEntry.readAll',
  /** Pianificazione di tutta l'azienda (consolida `canSeeWeeklyPlanner` + `canSeeAllPlanning`). */
  planningReadAll: 'PlanningEntry.readAll',
  /**
   * Smart working di chiunque nel tenant. **Non esisteva** fino al 10-08-2026: era l'unico
   * modulo HR senza see-all proprio, e `pending-approval` apriva il perimetro org-wide solo
   * a superadmin e `isHR`. Nel frattempo qui si usava `LeaveRequest.readAll` come sostituto,
   * che è il claim di un altro modulo: chi aveva le assenze e non lo smart working si vedeva
   * la coda intera, chi aveva solo lo smart working se la vedeva ristretta. Ora il claim c'è
   * e `pending-approval` lo legge.
   */
  smartWorkingReadAll: 'SmartWorkingRequest.readAll',
  /** Note spese di chiunque nel tenant (HrSpecialist, HrManager, PayrollSpecialist, FinanceReader). */
  expenseReadAll: 'ExpenseReport.readAll',
  /**
   * Decide le note spese anche fuori dalla propria gerarchia. I manager approvano **senza**
   * questo claim, per gerarchia: serve a chi non ha sottoposti ma deve poter decidere.
   */
  expenseApprove: 'ExpenseReport.approve',
  /**
   * Liquida il rimborso. Separato da `approve` di proposito: **chi liquida non è chi approva**,
   * ed è la ragione per cui `PayrollSpecialist` ha questo e non quello.
   */
  expensePay: 'ExpenseReport.pay',
  /**
   * Amministra gli accessi al portale clienti: account, documenti, pagine su misura (BE
   * `ClientPortalAccountResourceDefinition.ManageClaim`). Ce l'hanno `TenantAdmin` ed `Executive`.
   */
  clientPortalManage: 'client.portal.manage',
  /** Direzione HR: interruttore generale, passa ogni gate org-wide di ogni modulo. */
  hr: 'isHR',
  /** Amministratore applicativo. */
  admin: 'isAdmin',
  subTenants: 'canManageSubTenants',
  /**
   * Vede le aziende del **proprio gruppo** holding, non tutta l'installazione (BE `v1.152.0`: il
   * gruppo si calcola dal più alto antenato *sotto* il tenant di piattaforma, perché il master è la
   * radice di tutti e «radice + sottoalbero» avrebbe significato «tutte le aziende di tutti i
   * clienti»). Potato da `Staff`, `Admin` e `Marketing`, che lo avevano e non entravano: resta su
   * `SuperAdmin`, `CanSeeAllCustomers`, `CapoArea`.
   */
  allTenants: 'canSeeAllTenants',
  sprints: 'canManageSprints',
  vps: 'canSeeVps',
  fleetBookingApprove: 'fleet.booking.approve.write',
  fleetBookingAssign: 'fleet.booking.assign.write',
  travelBookingApprove: 'travel.booking.approve.write',
  travelBookingAssign: 'travel.booking.assign.write',
  leaveDocumentWaive: 'leave.document.waive',
  /**
   * Muovere il magazzino: carico, prelievo, trasferimento, inventario (BE `warehouse.operate`).
   *
   * ⚠️ Distinto dalla **lettura** (`WarehouseStock.read`), e la distinzione non è formale: un
   * Auditor e un capo che controlla le scorte leggono il magazzino e non lo muovono. Senza questa
   * separazione i quattro bottoni operativi comparirebbero anche a loro, e il primo click darebbe
   * un 403 — cioè l'app prometterebbe un'azione che il server nega.
   */
  warehouseOperate: 'warehouse.operate',
  /**
   * «Apri in MaeSpace» (`POST /api/maespace/sso-link`, BE v1.242.0). Di serie sui profili
   * `SelfServiceEmployee` e `SelfServiceTechnician`; da solo non basta, serve anche il modulo
   * `maespace` acceso per l'azienda.
   */
  maespaceSso: 'maespace.sso',
  /** Collegare un assistente (Claude) via MCP: lettura con i propri permessi (`docs/FE-TO-BE-MCP-v1.md`). */
  mcpUse: 'mcp.use',
  /** Concedere all'assistente anche la scrittura (solo i tool curati del server). */
  mcpWrite: 'mcp.write',
  /** Vedere e revocare gli assistenti collegati di tutta l'azienda. */
  mcpManage: 'mcp.manage',
} as const;

/**
 * Marker di sessione portale clienti (BE v1.100). Sta a parte da `CLAIM` perché non è un
 * permesso ma un'**identità**: decide quale shell montare, non cosa si può fare. Arriva nel
 * `claimsList` della risposta di login, come tutti gli altri.
 */
export const CLAIM_PORTAL = 'clientPortal';

export type ClaimName = (typeof CLAIM)[keyof typeof CLAIM];

/**
 * Nomi precedenti al 06-08-2026, accettati finché circolano token emessi prima del deploy.
 * Non aggiungerne: un alias permanente è un secondo nome per la stessa cosa, e il prossimo
 * che legge non sa quale dei due è quello vero.
 */
const LEGACY_ALIASES: Record<string, readonly string[]> = {
  [CLAIM.leaveReadAll]: ['canSeeAllUsersAbsence'],
  [CLAIM.attendanceReadAll]: ['canSeeAllUsersAttendance'],
  [CLAIM.timesheetReadAll]: ['canSeeAllReporting'],
  [CLAIM.planningReadAll]: ['canSeeAllPlanning', 'canSeeWeeklyPlanner'],
};

/** `claimsList` dell'envelope → array. Tollera spazi, virgole doppie e stringa vuota. */
export function parseClaimsList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(',').map((c) => c.trim()).filter(Boolean);
}

/** Il claim c'è (o c'è il suo nome pre-rinomina, su un token non ancora rinnovato). */
export function hasClaim(claims: readonly string[], claim: string): boolean {
  if (claims.includes(claim)) return true;
  const legacy = LEGACY_ALIASES[claim];
  return legacy ? legacy.some((l) => claims.includes(l)) : false;
}

/**
 * Il perimetro org-wide di un modulo, con la **stessa formula del BE**
 * (`DomainAccessGuard.CanSeeAllAsync`): super-admin, oppure Direzione HR, oppure il claim
 * del modulo. Tenerla in un posto solo è ciò che impedisce alle nostre viste di divergere
 * dalle code che il server calcola.
 */
export function canSeeAll(
  claims: readonly string[],
  isSuperadmin: boolean,
  moduleClaim: ClaimName,
): boolean {
  return isSuperadmin || hasClaim(claims, CLAIM.hr) || hasClaim(claims, moduleClaim);
}
